import React from 'react';
import type { FixedDaysPoolParticipantSummary, FixedDaysSummary } from '@/types/fixedDays.types';

interface FixedDaysBarProps {
  data: Partial<FixedDaysSummary> | undefined;
  title: string;
  participantSummary?: FixedDaysPoolParticipantSummary;
  participantName?: string | null;
  hasCmOnhire?: boolean;
  isLoading?: boolean;
  isLoadingParticipantsSummary?: boolean;
  /** Tighter padding and type — e.g. dashboard widget */
  compact?: boolean;
  className?: string;
}

export const FixedDaysBar: React.FC<FixedDaysBarProps> = ({
  data,
  title,
  participantSummary,
  participantName,
  hasCmOnhire = false,
  isLoading = false,
  isLoadingParticipantsSummary = false,
  compact = false,
  className = '',
}) => {
  const percentage = data?.pwFixedPercent || 0;
  const estimatedRate = data?.estimatedRate || 0;
  const clampedPercent = Math.min(Math.max(percentage, 0), 100);
  const fadeStart = Math.min(clampedPercent + 6, 100);

  const isFullYear = title?.toLowerCase().includes('full year');
  const isQuarter = /^Q[1-4]/i.test(title || '');

  const cardBgStyle = {
    backgroundImage:
      isFullYear || isQuarter
        ? 'none'
        : `linear-gradient(90deg, rgba(59,130,246,0.12) 0%, rgba(59,130,246,0.12) ${clampedPercent}%, rgba(59,130,246,0.06) ${fadeStart}%, rgba(255,255,255,0) 100%)`,
  };

  const formatNumber = (value: number | null | undefined, decimals = 0) => {
    if (value === null || value === undefined) return '--';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  };

  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '--';
    return `$${formatNumber(value, 0)}`;
  };

  if (isLoading) {
    const shell = compact
      ? 'mb-1 rounded-lg border border-gray-200 bg-white px-2 py-1 dark:border-dark-500 dark:bg-dark-800 sm:mb-1.5 sm:px-3 sm:py-1.5 xl:px-3 xl:py-1.5'
      : 'mb-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 dark:border-dark-500 dark:bg-dark-800 sm:mb-2 sm:rounded-xl sm:px-4 sm:py-2.5 xl:px-4 xl:py-2';

    return (
      <div className={`${shell} ${className}`}>
        <div className="animate-pulse">
          {/* Compact skeleton: mobile only */}
          <div className={`md:hidden ${compact ? 'space-y-0.5' : 'space-y-1'}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="h-4 w-20 rounded bg-gray-200 dark:bg-dark-700 sm:h-5 sm:w-24" />
              <div className="h-3 w-28 rounded bg-gray-200 dark:bg-dark-700 sm:h-3.5" />
            </div>
            <div className="h-1 rounded-full bg-gray-200 dark:bg-dark-700 sm:h-1.5" />
            <div className="grid grid-cols-3 gap-1.5 pt-0.5">
              <div className="space-y-1">
                <div className="h-2.5 w-full rounded bg-gray-200 dark:bg-dark-700" />
                <div className="h-3.5 w-12 rounded bg-gray-200 dark:bg-dark-700" />
              </div>
              <div className="space-y-1 text-right">
                <div className="ml-auto h-3.5 w-14 rounded bg-gray-200 dark:bg-dark-700" />
                <div className="ml-auto h-2.5 w-full rounded bg-gray-200 dark:bg-dark-700" />
              </div>
              <div className="space-y-1 text-right">
                <div className="ml-auto h-3.5 w-14 rounded bg-gray-200 dark:bg-dark-700" />
                <div className="ml-auto h-2.5 w-full rounded bg-gray-200 dark:bg-dark-700" />
              </div>
            </div>
          </div>
          {/* md–xl: dense single header row + bar + rates */}
          <div className={`hidden md:block xl:hidden ${compact ? 'space-y-0.5' : 'space-y-1'}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="h-4 w-14 rounded bg-gray-200 dark:bg-dark-700 sm:h-5 sm:w-20" />
              <div className="flex flex-wrap justify-end gap-x-3 gap-y-0.5">
                <div className="h-3 w-28 rounded bg-gray-200 dark:bg-dark-700" />
                <div className="h-3 w-24 rounded bg-gray-200 dark:bg-dark-700" />
              </div>
            </div>
            <div className="h-1 rounded-full bg-gray-200 dark:bg-dark-700" />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="mb-0 h-4 w-16 rounded bg-gray-200 dark:bg-dark-700 sm:h-5 sm:w-20" />
                <div className="mt-0.5 h-2.5 w-full max-w-[7rem] rounded bg-gray-200 dark:bg-dark-700" />
              </div>
              <div className="text-right">
                <div className="mb-0 ml-auto h-4 w-16 rounded bg-gray-200 dark:bg-dark-700 sm:h-5 sm:w-20" />
                <div className="ml-auto mt-0.5 h-2.5 w-full max-w-[7rem] rounded bg-gray-200 dark:bg-dark-700" />
              </div>
            </div>
          </div>
          {/* xl+: wide row */}
          <div
            className={`hidden xl:grid xl:grid-cols-12 xl:items-center ${compact ? 'gap-1.5' : 'gap-2'}`}
          >
            <div className="xl:col-span-3">
              <div className="h-5 w-20 rounded bg-gray-200 dark:bg-dark-700"></div>
            </div>
            <div className="xl:col-span-3">
              <div className="mb-0.5 flex flex-wrap items-center justify-end gap-x-3 gap-y-0 text-xs">
                <div className="h-3 w-28 rounded bg-gray-200 dark:bg-dark-700"></div>
                <div className="h-3 w-24 rounded bg-gray-200 dark:bg-dark-700"></div>
              </div>
              <div className="h-1 w-full rounded-full bg-gray-200 dark:bg-dark-700"></div>
            </div>
            <div className="xl:col-span-6">
              <div className="grid grid-cols-2 gap-2">
                <div className="text-left xl:text-right">
                  <div className="mb-0 h-4 w-20 rounded bg-gray-200 dark:bg-dark-700 sm:h-5 sm:w-24 xl:ml-auto"></div>
                  <div className="mt-0.5 h-2.5 w-28 rounded bg-gray-200 dark:bg-dark-700 xl:ml-auto"></div>
                </div>
                <div className="text-right">
                  <div className="mb-0 ml-auto h-4 w-20 rounded bg-gray-200 dark:bg-dark-700 sm:h-5 sm:w-24"></div>
                  <div className="ml-auto mt-0.5 h-2.5 w-32 rounded bg-gray-200 dark:bg-dark-700"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const shell = compact
    ? 'mb-1 rounded-lg border px-2 py-1 transition-colors sm:mb-1.5 sm:px-3 sm:py-1.5 xl:px-3 xl:py-1.5'
    : 'mb-1.5 rounded-lg border px-2.5 py-1.5 transition-colors sm:mb-2 sm:rounded-xl sm:px-4 sm:py-2.5 xl:px-4 xl:py-2';

  return (
    <div
      className={`${shell} ${
        isFullYear
          ? 'border-gray-100 bg-gray-50 dark:border-dark-600 dark:bg-dark-700'
          : isQuarter
            ? 'dark:bg-dark-750 border-gray-100 bg-gray-50/50 dark:border-dark-600'
            : 'border-gray-200 bg-white dark:border-dark-500 dark:bg-dark-800'
      } ${className}`}
      style={cardBgStyle}
    >
      {/* Compact: mobile only — title + weighted row, full-width bar, 3-column footer (type ~ Recent Fixtures density) */}
      <div className={`md:hidden ${compact ? 'space-y-1' : 'space-y-1.5'}`}>
        <div className="flex min-w-0 items-start justify-between gap-2">
          <h4
            className={`min-w-0 flex-1 truncate font-semibold tabular-nums text-gray-900 dark:text-white ${
              compact ? 'text-sm sm:text-base' : 'text-base sm:text-lg'
            }`}
          >
            {title}
          </h4>
          <div
            className={`flex shrink-0 flex-wrap items-center justify-end gap-x-1 leading-snug text-gray-500 dark:text-gray-400 ${
              compact ? 'text-[11px] sm:text-xs' : 'text-xs sm:text-sm'
            }`}
          >
            <span className="opacity-60">Weighted Days:</span>
            <span className="font-semibold text-gray-700 dark:text-gray-300">
              {formatNumber(percentage, 0)}%
            </span>
          </div>
        </div>
        <div
          className={`w-full overflow-hidden rounded-full bg-gray-200 dark:bg-dark-700 ${compact ? 'h-1' : 'h-1.5'}`}
        >
          <div
            className="h-full rounded-full bg-primary-600 transition-all duration-500 dark:bg-primary-500"
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
        <div
          className={`grid grid-cols-3 gap-y-0 border-t border-gray-100 dark:border-dark-600 ${
            compact ? 'gap-x-1.5 pt-1' : 'gap-x-2 pt-1.5'
          }`}
        >
          <div className="min-w-0 border-r border-gray-100 pr-1 dark:border-dark-600">
            <div
              className={`leading-tight text-gray-500 opacity-70 dark:text-gray-400 ${
                compact ? 'text-[11px] sm:text-xs' : 'text-xs sm:text-sm'
              }`}
            >
              Free Rates:
            </div>
            <div
              className={`font-semibold tabular-nums leading-tight text-gray-900 dark:text-white ${
                compact ? 'text-xs sm:text-sm' : 'text-sm sm:text-base'
              }`}
            >
              {percentage === 100 ? '--' : formatCurrency(estimatedRate)}
            </div>
          </div>
          <div className="min-w-0 border-r border-gray-100 px-0.5 text-right dark:border-dark-600 sm:px-1">
            <div
              className={`font-semibold tabular-nums leading-none text-gray-900 dark:text-white ${
                compact ? 'text-xs sm:text-sm' : 'text-sm sm:text-base'
              }`}
            >
              {formatCurrency(data?.spotRateInclRelet)}
            </div>
            <div className="mt-0.5 text-xs leading-tight text-gray-500 dark:text-gray-400">
              Fixed Rates (Spot)
            </div>
          </div>
          <div className="min-w-0 pl-0.5 text-right sm:pl-1">
            <div
              className={`font-semibold tabular-nums leading-none text-gray-900 dark:text-white ${
                compact ? 'text-xs sm:text-sm' : 'text-sm sm:text-base'
              }`}
            >
              {formatCurrency(data?.watEstimated)}
            </div>
            <div className="mt-0.5 text-xs leading-tight text-gray-500 dark:text-gray-400">
              Weighted Avg. Total
            </div>
          </div>
        </div>
      </div>

      {/* md–xl: short card — title + metrics one row, thin bar, tight rate row */}
      <div className={`hidden md:block xl:hidden ${compact ? 'space-y-0.5' : 'space-y-1'}`}>
        <div className="flex min-w-0 items-start justify-between gap-2">
          <h4
            className={`shrink-0 font-semibold tabular-nums text-gray-900 dark:text-white ${
              compact ? 'text-xs sm:text-sm' : 'text-sm sm:text-base'
            }`}
          >
            {title}
          </h4>
          <div className="min-w-0 text-right">
            <div className="flex flex-wrap items-baseline justify-end gap-x-3 gap-y-0 text-[11px] leading-tight text-gray-500 dark:text-gray-400 sm:text-xs">
              <span className="inline-flex flex-wrap items-baseline justify-end gap-x-1">
                <span className="opacity-60">Weighted Days:</span>
                <span className="font-semibold text-gray-700 dark:text-gray-300">
                  {formatNumber(percentage, 0)}%
                </span>
              </span>
              <span className="inline-flex flex-wrap items-baseline justify-end gap-x-1">
                <span className="opacity-60">Free Rates:</span>
                <span className="font-semibold tabular-nums text-gray-700 dark:text-gray-300">
                  {percentage === 100 ? '--' : formatCurrency(estimatedRate)}
                </span>
              </span>
            </div>
          </div>
        </div>
        <div className="h-1 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-dark-700">
          <div
            className="h-full rounded-full bg-primary-600 transition-all duration-500 dark:bg-primary-500"
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
        <div className={`grid grid-cols-2 gap-y-0 ${compact ? 'gap-x-2' : 'gap-x-3'}`}>
          <div className="min-w-0 text-left">
            <div
              className={`font-semibold tabular-nums leading-none text-gray-900 dark:text-white ${
                compact ? 'text-xs sm:text-sm' : 'text-sm sm:text-base'
              }`}
            >
              {formatCurrency(data?.spotRateInclRelet)}
            </div>
            <span className="mt-0 block text-[10px] leading-tight text-gray-500 dark:text-gray-400 sm:text-[11px]">
              Fixed Rates (Spot)
            </span>
          </div>
          <div className="min-w-0 text-right">
            <div
              className={`font-semibold tabular-nums leading-none text-gray-900 dark:text-white ${
                compact ? 'text-xs sm:text-sm' : 'text-sm sm:text-base'
              }`}
            >
              {formatCurrency(data?.watEstimated)}
            </div>
            <span className="mt-0 block text-[10px] leading-tight text-gray-500 dark:text-gray-400 sm:text-[11px]">
              Weighted Avg. Total
            </span>
          </div>
        </div>
      </div>

      {/* xl+: single horizontal row */}
      <div
        className={`hidden xl:grid xl:grid-cols-12 xl:items-center ${compact ? 'gap-1.5' : 'gap-2'}`}
      >
        <h4
          className={`font-semibold tabular-nums text-gray-900 dark:text-white xl:col-span-3 ${
            compact ? 'text-sm' : 'text-base'
          }`}
        >
          {title}
        </h4>
        <div className="space-y-0.5 xl:col-span-3">
          <div
            className={`flex flex-wrap items-center justify-end gap-x-3 gap-y-0 text-gray-500 dark:text-gray-400 ${
              compact ? 'text-[11px]' : 'text-xs'
            }`}
          >
            <span className="inline-flex flex-wrap items-center justify-end gap-x-1">
              <span className="opacity-60">Weighted Days:</span>
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {formatNumber(percentage, 0)}%
              </span>
            </span>
            <span className="inline-flex flex-wrap items-center justify-end gap-x-1">
              <span className="opacity-60">Free Rates:</span>
              <span className="font-semibold tabular-nums text-gray-700 dark:text-gray-300">
                {percentage === 100 ? '--' : formatCurrency(estimatedRate)}
              </span>
            </span>
          </div>
          <div className="h-1 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-dark-700">
            <div
              className="h-full rounded-full bg-primary-600 transition-all duration-500 dark:bg-primary-500"
              style={{ width: `${Math.min(percentage, 100)}%` }}
            />
          </div>
        </div>
        <div className="xl:col-span-6">
          <div className={`grid grid-cols-2 ${compact ? 'gap-1.5' : 'gap-2'}`}>
            <div className="min-w-0 text-left xl:text-right">
              <div
                className={`font-semibold tabular-nums leading-none text-gray-900 dark:text-white ${
                  compact ? 'text-sm' : 'text-base'
                }`}
              >
                {formatCurrency(data?.spotRateInclRelet)}
              </div>
              <span
                className={`mt-0 block leading-tight text-gray-500 dark:text-gray-400 ${
                  compact ? 'text-[11px]' : 'text-xs'
                }`}
              >
                Fixed Rates (Spot)
              </span>
            </div>
            <div className="min-w-0 text-right">
              <div
                className={`font-semibold tabular-nums leading-none text-gray-900 dark:text-white ${
                  compact ? 'text-sm' : 'text-base'
                }`}
              >
                {formatCurrency(data?.watEstimated)}
              </div>
              <span
                className={`mt-0 block leading-tight text-gray-500 dark:text-gray-400 ${
                  compact ? 'text-[11px]' : 'text-xs'
                }`}
              >
                Weighted Avg. Total
              </span>
            </div>
          </div>
        </div>
      </div>

      {((participantSummary && participantName) ||
        hasCmOnhire ||
        (participantName && isLoadingParticipantsSummary)) && (
        <div
          className={`grid ${compact ? 'mt-1 gap-1 sm:mt-1.5 sm:gap-1.5' : 'mt-1.5 gap-1.5 sm:mt-2 sm:gap-2'} ${
            hasCmOnhire && participantName ? 'lg:grid-cols-2' : ''
          }`}
        >
          {hasCmOnhire && (
            <div className="h-full rounded-md border border-orange-200 bg-orange-50/70 px-2 py-1.5 text-[10px] dark:border-orange-400/50 dark:bg-orange-500/15 sm:rounded-lg sm:px-3 sm:py-2 sm:text-xs">
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <div>
                  <div className="mb-0.5 text-gray-500 dark:text-gray-400">On Hire Days</div>
                  <div className="text-xs font-semibold text-gray-900 dark:text-white">
                    {formatNumber(data?.cmOnhireDays, 0)}
                  </div>
                </div>
                <div>
                  <div className="mb-0.5 text-gray-500 dark:text-gray-400">On Hire TCE</div>
                  <div className="text-xs font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(data?.cmOnhireTce)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {participantName && isLoadingParticipantsSummary && (
            <div className="h-full animate-pulse rounded-md border border-emerald-200 bg-emerald-50/70 px-2 py-1.5 text-[10px] dark:border-emerald-800 dark:bg-emerald-900/20 sm:rounded-lg sm:px-3 sm:py-2 sm:text-xs">
              <div className="mb-1 h-3 w-20 rounded bg-emerald-200 dark:bg-emerald-700 sm:mb-1.5 sm:w-24"></div>
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <div>
                  <div className="mb-0.5 h-3 w-20 rounded bg-gray-200 dark:bg-dark-600"></div>
                  <div className="h-4 w-16 rounded bg-gray-200 dark:bg-dark-600"></div>
                </div>
                <div>
                  <div className="mb-0.5 h-3 w-20 rounded bg-gray-200 dark:bg-dark-600"></div>
                  <div className="h-4 w-16 rounded bg-gray-200 dark:bg-dark-600"></div>
                </div>
              </div>
            </div>
          )}

          {participantName && !isLoadingParticipantsSummary && (
            <div className="h-full rounded-md border border-emerald-200 bg-emerald-50/70 px-2 py-1.5 text-[10px] dark:border-emerald-800 dark:bg-emerald-900/20 sm:rounded-lg sm:px-3 sm:py-2 sm:text-xs">
              <div className="mb-1 line-clamp-2 text-[10px] font-semibold uppercase leading-tight tracking-wide text-emerald-700 dark:text-emerald-300 sm:mb-1.5 sm:text-xs">
                {participantName ? participantName : 'Pool Participant'}
              </div>
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <div>
                  <div className="mb-0.5 text-gray-500 dark:text-gray-400">Period Pool Points</div>
                  <div className="text-xs font-semibold text-gray-900 dark:text-white">
                    {formatNumber(participantSummary?.periodPoolPoints, 2)}
                  </div>
                </div>
                <div>
                  <div className="mb-0.5 text-gray-500 dark:text-gray-400">Avg. Earnings</div>
                  <div className="text-xs font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(participantSummary?.avgEarnings)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
